import type { SaveTemplateModalProps } from "@v1/types/modalTypes";
import type { ComponentTemplateType } from "schemas/generated/componentTemplates";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import Input from "@v1/components/common/designSystem/Input";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import { useSaveComponentTemplate } from "@v1/hooks/useSaveComponentTemplate";
import { useModal } from "@v2/store/modalStore";

const COMPONENT_TEMPLATE_TYPE_OPTIONS = [
  {
    type: "component",
    title: "Component",
    description: "Components panel",
    imagePath: "/images/templates/saved/save-component-skeleton.svg",
  },
  {
    type: "section",
    title: "Section",
    description: "Sections panel",
    imagePath: "/images/templates/saved/save-section-skeleton.svg",
  },
  {
    type: "page",
    title: "Saved Page",
    description: "Template library",
    imagePath: "/images/templates/saved/save-page-skeleton.svg",
  },
];

const SaveItemSelectOptionButton = ({
  title,
  description,
  imagePath,
  onClick,
  isSelected,
}: {
  title: string;
  description: string;
  imagePath: string;
  onClick: () => void;
  isSelected: boolean;
}) => {
  return (
    <button
      onClick={onClick}
      className={twMerge(
        "border-0.5 hover:bg-selectable-selected-hover group flex flex-col overflow-hidden rounded-md px-3 pt-3 text-left",
        isSelected
          ? "bg-selectable-selected border-primary"
          : "border-border bg-white",
      )}
    >
      <div className="flex-1">
        <h3
          className={twMerge(
            "text-muted text-xs font-medium font-semibold",
            isSelected && "text-default",
          )}
        >
          {title}
        </h3>
        <p
          className={twMerge(
            "text-placeholder mb-2 text-sm",
            isSelected && "text-muted",
          )}
        >
          {description}
        </p>
      </div>
      <div className="relative mt-auto translate-y-2">
        <img
          src={imagePath}
          alt={title}
          className="w-full object-cover shadow-lg"
        />
      </div>
    </button>
  );
};

export const SaveTemplateModal = (props: SaveTemplateModalProps) => {
  const [selectedType, setSelectedType] =
    React.useState<ComponentTemplateType>("component");
  const modal = useModal();
  const [name, setName] = React.useState(props.initialName ?? "");
  const { isLoading, handleSubmit } = useSaveComponentTemplate(() => {
    modal.closeModal({});
  });
  const onSubmit = async () => {
    void handleSubmit(name, selectedType);
  };

  return (
    <Modal
      isOpen={true}
      onOpenChange={() => modal.closeModal({ type: "saveTemplateModal" })}
      size="base"
      title="New Saved Item"
      description="Save to build faster and share templates with others"
      footer={
        <div className="flex gap-2">
          <DeprecatedButton
            variant="secondary"
            size="base"
            onClick={() => modal.closeModal({})}
          >
            Cancel
          </DeprecatedButton>
          <DeprecatedButton
            variant="primary"
            type="submit"
            disabled={name.trim().length === 0}
            size="base"
            isLoading={isLoading}
            onClick={() => void onSubmit()}
          >
            Save
          </DeprecatedButton>
        </div>
      }
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Item Name"
        size="base"
      />
      <div className="flex gap-3">
        {COMPONENT_TEMPLATE_TYPE_OPTIONS.map((option) => (
          <SaveItemSelectOptionButton
            key={option.title}
            title={option.title}
            description={option.description}
            imagePath={option.imagePath}
            onClick={() => {
              return setSelectedType(option.type as ComponentTemplateType);
            }}
            isSelected={selectedType === option.type}
          />
        ))}
      </div>
    </Modal>
  );
};
